/**
 * CustomerNotesPanel — #715, staff-only notes on the parent record.
 *
 * Kylie (HTHD receptionist) asked for this by name: "If a parent's talked
 * to me and they're frustrated or something, I would also put that in a
 * note… but I don't want to be writing notes and it goes to the pet
 * parent." Both halves matter, so the panel states the second one on
 * screen rather than leaving the staffer to trust us — a promise the user
 * cannot see is a promise they will hedge against by not writing the note.
 *
 * DESIGN-PHILOSOPHY compliance:
 *  · P2 (the next action is obvious) — one primary button; the empty state
 *    teaches what belongs here instead of saying "No notes."
 *  · P5 (never lie about data) — an author we could not resolve renders
 *    "Unknown staff", never a blank that reads as "nobody"; an imported
 *    note is labelled as imported rather than passed off as observed. The
 *    error state is distinguishable from the empty state, so
 *    empty-because-it-failed can never look like a quiet customer.
 *  · Five states — loading, error (with recovery), empty, default, and the
 *    submitting/optimistic state.
 *  · Tokens only; no raw hex, no ad-hoc spacing.
 *
 * Extracted from CustomerDetail's inline card, which had no author, no
 * loading state, no error state, and a bare "No notes yet."
 *
 * Sawyer 11 client-notes-agreements (F1) — Sawyer: "we can pin it to the
 * top ... And if new notes are made, some kind of flag". Pinned notes form
 * their own group first, with who pinned them and when; Pin / Unpin sit on
 * each note (optimistic, reverting with an inline error on failure); notes
 * new to me carry a "New" tag for this view, and loading the panel marks
 * them seen (the desk flag clears). The date-ordered history is capped at
 * the newest 5 under the pinned group (refuter C10); Add note is disabled
 * while the history failed to load (#761 item 2). Against an older server
 * (no `pinned_at` key on any note) the panel renders without pin controls.
 *
 * Reading comes first (review round 1): the pinned group is the first thing
 * under the card title, on a 375px phone as on the desk monitor. Once the
 * client has notes, the compose box folds behind a 44px "Add a note" button
 * in the card header (P2: the action stays at the top without pushing the
 * notes below the fold); with no notes yet it is open, because writing the
 * first one is the only thing to do. Focus follows the box: opening it lands
 * in the textarea, and Cancel or a successful save hands focus back to
 * "Add a note" (never to <body>). The privacy promise sits inside the box,
 * where the staffer types, and at the foot of the card while it is folded.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Alert, Button } from '@ferro/ui';
import {
  addCustomerNote,
  fetchCustomerNotes,
  humanizeApiError,
  readSession,
  type CustomerNote,
} from '../../lib/api.js';
// Sawyer 11 client-notes-agreements (F1)
import { markCustomerNotesSeen, setCustomerNotePinned } from '../../lib/client-notes-api.js';
import { compareNewest, comparePinned, noteAuthor, pinAttribution } from './client-note-format.js';
import './ClientNotes.css';

/** Sawyer F1 — date-ordered notes shown under the pinned group before "Show all". */
export const NOTE_HISTORY_CAP = 5;

/** Sawyer F1 — does this payload come from a server that knows about pins? */
export function payloadHasPinAttribution(notes: ReadonlyArray<CustomerNote>): boolean {
  return notes.some((n) => 'pinned_at' in n);
}

/** Sawyer F1 — the newest note on screen: what "seen" may cover, and no further. */
export function newestCreatedAt(notes: ReadonlyArray<CustomerNote>): string | null {
  let best: string | null = null;
  for (const n of notes) {
    if (n.id.startsWith('optimistic-')) continue;
    if (best === null || Date.parse(n.created_at) > Date.parse(best)) best = n.created_at;
  }
  return best;
}

export interface CustomerNotesPanelProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly customerId: string;
  /**
   * Whether this staffer holds `customers.notes:write:all`. UX gate only —
   * the server is the authority and will 403 regardless. Read-only roles
   * still see the history; they just get no compose box.
   */
  readonly canWrite?: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; notes: CustomerNote[] };

/**
 * `humanizeApiError` maps a raw server error STRING; a caught value is
 * `unknown`. Narrowing here (rather than passing the value through) is the
 * house pattern — see Incidents.tsx / Reports.tsx — and it matters: #711
 * made the humanizer map anything it does not recognize to generic copy,
 * so handing it a non-string would silently produce that copy for every
 * failure and hide real messages the server does send.
 */
function messageOf(err: unknown): string {
  return humanizeApiError(err instanceof Error ? err.message : null);
}

/** Human timestamp. Raw ISO strings are an anti-pattern in this app. */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown date';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Attribution line. P5: we never render an empty author as if the note
 * authored itself, and we never invent a name we do not have.
 */
function attribution(note: CustomerNote): string {
  // Sawyer #1296 — one author rule for every note surface (client-note-format).
  return noteAuthor(note);
}

export function CustomerNotesPanel({
  apiUrl,
  token,
  customerId,
  canWrite = true,
}: CustomerNotesPanelProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Sawyer 11 client-notes-agreements (F1)
  const [pinBusy, setPinBusy] = useState<string | null>(null);
  const [pinError, setPinError] = useState<{ noteId: string; message: string } | null>(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  // Review round 1 — the compose box folds once there is something to read.
  const [composeOpen, setComposeOpen] = useState(false);
  const [focusTarget, setFocusTarget] = useState<'body' | 'compose-button' | null>(null);
  const composeButtonRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const res = await fetchCustomerNotes(apiUrl, token, customerId);
      setState({ kind: 'ready', notes: res.notes });
      // Sawyer F1 — I have now seen this client's notes up to the newest one
      // on screen. Fire-and-forget: a missed "seen" only means the desk flag
      // shows again; it is never a reason to show an error here.
      if (res.notes.some((n) => n.is_new === true)) {
        const through = newestCreatedAt(res.notes);
        if (through) void markCustomerNotesSeen(apiUrl, token, customerId, through);
      }
    } catch (err) {
      setState({ kind: 'error', message: messageOf(err) });
    }
  }, [apiUrl, token, customerId]);

  // Sawyer F1 — pin / unpin, optimistic with rollback (P4). The server
  // answers with the note it stored, attribution included.
  const togglePin = async (note: CustomerNote): Promise<void> => {
    if (pinBusy) return;
    const nextPinned = !note.pinned;
    const me = readSession().staff;
    const myName = me ? [me.first_name, me.last_name].filter(Boolean).join(' ') || 'You' : 'You';
    setPinBusy(note.id);
    setPinError(null);
    const replace = (next: CustomerNote) =>
      setState((cur) =>
        cur.kind === 'ready'
          ? { ...cur, notes: cur.notes.map((n) => (n.id === next.id ? next : n)) }
          : cur,
      );
    replace({
      ...note,
      pinned: nextPinned,
      pinned_at: nextPinned ? new Date().toISOString() : null,
      pinned_by_staff_id: nextPinned ? (me?.id ?? null) : null,
      pinned_by_name: nextPinned ? myName : null,
    });
    try {
      const res = await setCustomerNotePinned(apiUrl, token, customerId, note.id, nextPinned);
      replace({ ...res.note, is_new: note.is_new });
    } catch {
      replace(note);
      setPinError({ noteId: note.id, message: "Couldn't update the pin. Try again." });
    } finally {
      setPinBusy(null);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  // Open when asked, or when there is nothing to read yet (the first note is
  // the only thing to do). Read-only roles never get the box.
  const hasNotes = state.kind === 'ready' && state.notes.length > 0;
  const formOpen = canWrite && (composeOpen || (state.kind === 'ready' && state.notes.length === 0));

  // Focus follows the box. Handing focus back to "Add a note" happens only
  // when focus went down with the form (it now sits on <body>): a staffer who
  // moved on to a Pin button mid-save keeps her place.
  useEffect(() => {
    if (focusTarget === null) return;
    const el = focusTarget === 'body' ? bodyRef.current : composeButtonRef.current;
    if (!el) return;
    if (focusTarget === 'compose-button') {
      const active = document.activeElement;
      if (active && active !== document.body) {
        setFocusTarget(null);
        return;
      }
    }
    el.focus();
    setFocusTarget(null);
  }, [focusTarget, formOpen]);

  const openCompose = (): void => {
    setComposeOpen(true);
    setFocusTarget('body');
  };

  const cancelCompose = (): void => {
    // The draft stays in state: reopening the box brings it back.
    setComposeOpen(false);
    setSubmitError(null);
    setFocusTarget('compose-button');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = body.trim();
    if (trimmed.length === 0) {
      setSubmitError('Write something before saving the note.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    // Hold the box open through the save, even when this is the first note
    // (the optimistic row would otherwise fold it mid-flight).
    setComposeOpen(true);

    // Optimistic insert so the desk gets sub-100ms acknowledgement (P4).
    // author_name is 'You' until the server answers with the real name —
    // an honest placeholder, not a guess at what the server will say.
    const optimistic: CustomerNote = {
      id: `optimistic-${Date.now()}`,
      customer_id: customerId,
      staff_id: null,
      author_name: 'You',
      author_role: null,
      body: trimmed,
      pinned,
      source: 'staff',
      source_ref: null,
      created_at: new Date().toISOString(),
    };
    const previous = state;
    // Sawyer F1 — the list is grouped and sorted at render, so the new note
    // lands below the pinned group (unless it is pinned) before and after
    // the server answers: no wobble.
    setState((current) =>
      current.kind === 'ready'
        ? { ...current, notes: [optimistic, ...current.notes] }
        : { kind: 'ready', notes: [optimistic] },
    );
    setBody('');
    setPinned(false);

    try {
      const res = await addCustomerNote(apiUrl, token, customerId, {
        body: trimmed,
        pinned: optimistic.pinned,
      });
      setState((current) =>
        current.kind === 'ready'
          ? {
              ...current,
              notes: current.notes.map((n) =>
                n.id === optimistic.id ? res.note : n,
              ),
            }
          : current,
      );
      // Saved: the note is in the list, the box folds, focus returns to
      // "Add a note".
      setComposeOpen(false);
      setFocusTarget('compose-button');
    } catch (err) {
      setSubmitError(messageOf(err));
      // Put the text back in the box — losing what someone typed is worse
      // than the failure itself.
      setBody(trimmed);
      setPinned(optimistic.pinned);
      setState(previous);
    } finally {
      setSubmitting(false);
    }
  };

  // Sawyer 11 client-notes-agreements (F1) — one note row in the grouped view.
  const renderNote = (n: CustomerNote): JSX.Element => {
    const pin = pinAttribution(n);
    const optimistic = n.id.startsWith('optimistic-');
    const busy = pinBusy === n.id;
    return (
      <li key={n.id} className="ferro-timeline__item ferro-clientnotes__panel-item" data-testid={`customer-note-${n.id}`}>
        <span className="ferro-timeline__when">
          {formatWhen(n.created_at)} · {attribution(n)}
        </span>
        <div className="ferro-clientnotes__note-main">
        <span className="ferro-timeline__what">
          {n.is_new ? (
            <span className="ferro-clientnotes__new" data-testid={`customer-note-new-${n.id}`}>
              New
            </span>
          ) : null}
          {n.body}
        </span>
        {pin ? (
          <span className="ferro-clientnotes__meta" data-testid={`customer-note-pin-attribution-${n.id}`}>
            {pin}
          </span>
        ) : null}
        {canWrite && !optimistic && 'pinned_at' in n ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-clientnotes__pin"
            onClick={() => void togglePin(n)}
            disabled={busy}
            aria-label={`${n.pinned ? 'Unpin' : 'Pin'} note: ${n.body.slice(0, 60)}`}
            data-testid={`customer-note-${n.pinned ? 'unpin' : 'pin'}-${n.id}`}
          >
            {busy ? 'Saving…' : n.pinned ? 'Unpin' : 'Pin'}
          </button>
        ) : null}
        {pinError?.noteId === n.id ? (
          <span className="ferro-clientnotes__pin-error" role="alert" data-testid={`customer-note-pin-error-${n.id}`}>
            {pinError.message}
          </span>
        ) : null}
        </div>
      </li>
    );
  };

  // Pinned group first (newest pin first), then the date-ordered history,
  // capped at NOTE_HISTORY_CAP until "Show all".
  const renderGrouped = (notes: ReadonlyArray<CustomerNote>): JSX.Element => {
    const pinnedNotes = notes.filter((n) => n.pinned).sort(comparePinned);
    const rest = notes.filter((n) => !n.pinned).sort(compareNewest);
    const visibleRest = showAllHistory ? rest : rest.slice(0, NOTE_HISTORY_CAP);
    return (
      <div className="ferro-clientnotes__panel" data-testid="customer-notes-list">
        {pinnedNotes.length > 0 ? (
          <section aria-label="Pinned notes" data-testid="customer-notes-pinned">
            <h3 className="ferro-clientnotes__group-title">Pinned</h3>
            <ul className="ferro-timeline">{pinnedNotes.map(renderNote)}</ul>
          </section>
        ) : null}
        {rest.length > 0 ? (
          <section aria-label="Staff note history" data-testid="customer-notes-history">
            {pinnedNotes.length > 0 ? (
              <h3 className="ferro-clientnotes__group-title">All notes</h3>
            ) : null}
            <ul className="ferro-timeline">{visibleRest.map(renderNote)}</ul>
            {rest.length > NOTE_HISTORY_CAP ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-clientnotes__more"
                aria-expanded={showAllHistory}
                onClick={() => setShowAllHistory((v) => !v)}
                data-testid="customer-notes-show-all"
              >
                {showAllHistory ? 'Show fewer notes' : `Show all ${rest.length} notes`}
              </button>
            ) : null}
          </section>
        ) : null}
      </div>
    );
  };

  // The guarantee, said out loud (see the header comment): inside the box
  // where the staffer types, else at the foot of the card.
  const privacyNote = (
    <p className="ferro-muted" data-testid="customer-notes-privacy-note">
      Only staff can see these. Nothing here is shown to the pet parent.
    </p>
  );

  return (
    <article
      className="ferro-dashboard__card"
      aria-label="Staff notes"
      data-testid="customer-notes-panel"
    >
      <header className="ferro-dashboard__cardHeader ferro-clientnotes__panel-header">
        <h2 className="ferro-dashboard__cardTitle">Staff notes</h2>
        <div className="ferro-clientnotes__header-end">
          {state.kind === 'ready' ? (
            <span className="ferro-dashboard__metric">{state.notes.length}</span>
          ) : null}
          {canWrite && !formOpen ? (
            <button
              ref={composeButtonRef}
              type="button"
              className="ferro-btn ferro-btn--ghost ferro-clientnotes__compose-open"
              onClick={openCompose}
              // Sawyer F1 / #761 item 2 — nothing to add to until the history
              // has loaded: a save then would turn a failed read into a
              // one-note list.
              disabled={state.kind !== 'ready'}
              data-testid="customer-note-compose-open"
            >
              Add a note
            </button>
          ) : null}
        </div>
      </header>

      {formOpen ? (
        <form
          className="ferro-form ferro-clientnotes__compose"
          onSubmit={handleSubmit}
          noValidate
          aria-label="Add a staff note"
          data-testid="customer-note-compose"
        >
          {privacyNote}
          <label className="ferro-field">
            <span className="ferro-field__label">Add note</span>
            <textarea
              ref={bodyRef}
              name="noteBody"
              className="ferro-field__input ferro-field__input--textarea"
              rows={3}
              placeholder="Frustrated at pickup Tuesday. Doesn't want bandanas on the dog."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="customer-note-body"
            />
          </label>
          <label className="ferro-checkbox">
            <input
              type="checkbox"
              checked={pinned}
              onChange={(e) => setPinned(e.target.checked)}
              data-testid="customer-note-pinned"
            />
            <span>Pin to top</span>
          </label>

          {submitError ? <Alert variant="error">{submitError}</Alert> : null}

          <div className="ferro-form__actions">
            {hasNotes && !submitting ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={cancelCompose}
                data-testid="customer-note-cancel"
              >
                Cancel
              </button>
            ) : null}
            <Button
              variant="primary"
              type="submit"
              // Sawyer F1 / #761 item 2 — no submit while the history failed to
              // load: a save then would turn a failed read into a one-note list.
              disabled={submitting || body.trim().length === 0 || state.kind === 'error'}
              data-testid="customer-note-submit"
            >
              {submitting ? 'Saving…' : 'Add note'}
            </Button>
          </div>
        </form>
      ) : null}

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status" data-testid="customer-notes-loading">
          Loading notes…
        </p>
      ) : null}

      {/* P5: the failure is visually distinct from "no notes", and it
          offers the recovery rather than naming the technology. */}
      {state.kind === 'error' ? (
        <div
          className="ferro-alert ferro-alert--error ferro-alert--dismissible"
          role="alert"
          data-testid="customer-notes-error"
        >
          <span>{state.message}</span>
          <button
            type="button"
            className="ferro-alert__dismiss"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        state.notes.length === 0 ? (
          <p className="ferro-muted" data-testid="customer-notes-empty">
            No staff notes yet. Add what the team should know before this
            parent's next visit — preferences, pickup quirks, anything you'd
            otherwise have to remember.
          </p>
        ) : (
          payloadHasPinAttribution(state.notes) ? (
            renderGrouped(state.notes)
          ) : (
          <ul
            className="ferro-timeline"
            aria-label="Staff note history"
            data-testid="customer-notes-list"
          >
            {state.notes.map((n) => (
              <li key={n.id} className="ferro-timeline__item">
                <span className="ferro-timeline__when">
                  {formatWhen(n.created_at)} · {attribution(n)}
                  {n.pinned ? ' · pinned' : ''}
                </span>
                <span className="ferro-timeline__what">{n.body}</span>
              </li>
            ))}
          </ul>
          )
        )
      ) : null}

      {formOpen ? null : privacyNote}
    </article>
  );
}
